import { isWithinInterval } from 'date-fns'

/** One salary code's aggregated total for a week, formatted as "HH:MM". */
export interface WeeklyActivitySum {
  name: string
  sum: string
}

/** Aggregated work totals for a single calendar week. */
export interface WeeklyWorkSum {
  /** Total work time across the week, formatted as "HH:MM". */
  totalWorkTime: string
  /** Per-salary-code sums, ordered like the month table, formatted as "HH:MM". */
  activitySums: WeeklyActivitySum[]
  /** Whether the week has any work to show (used to hide the info icon). */
  hasEntries: boolean
}

/** Hours to count towards the week the `date` falls in. */
interface WeeklySumEntry {
  date: Date | null
  totalWorkTime: string | null | undefined
  groupedActivitySums: {
    name: string | null
    sum: string | null
    salaryId?: string | null
  }[]
}

/** Minimal shape of a work-period group needed to compute weekly sums. */
export interface WeeklySumGroup {
  /**
   * The group's payroll hours split per ISO week by the backend, one entry per
   * week the shift touches, each holding only that week's share. A shift
   * running from Sunday night into Monday morning therefore lands in both
   * weeks for the hours it worked in each. Hours the month does not pay are
   * already gone: a work period no approval flow has signed off contributes no
   * entry, so a group of nothing but unapproved periods carries no weeks at
   * all (TSB-125), and activities rejected from the salary calculation were
   * dropped before the split. The list is therefore the whole truth about the
   * group's week — never fall back to summing the group itself.
   */
  weekSums: WeeklySumEntry[]
}

export interface WeekInterval {
  start: Date
  end: Date
}

/**
 * Parse a `TimeDelta` scalar ("07:15", "07:15:30", "-02:00") into seconds.
 * Hours are unbounded (the backend never emits a day component) and the sign,
 * when present, applies to the whole value. Returns 0 for unparseable input.
 */
function timedeltaToSeconds(value: string | null | undefined): number {
  if (!value) return 0

  const match = value.match(/^(-)?(\d+):(\d{2})(?::(\d{2}(?:\.\d+)?))?$/)
  if (!match) return 0

  const sign = match[1] ? -1 : 1
  const hours = Number(match[2] ?? 0)
  const minutes = Number(match[3] ?? 0)
  const seconds = Number(match[4] ?? 0)

  return sign * (hours * 3600 + minutes * 60 + seconds)
}

/**
 * Format a signed second count as "HH:MM" (hours may exceed two digits).
 * Seconds are truncated, not rounded, so a weekly total matches the sum of the
 * per-day values shown on the calendar (see `enforceHourMinuteString`).
 */
function secondsToHourMinute(total: number): string {
  const abs = Math.abs(total)
  const hours = Math.floor(abs / 3600)
  const minutes = Math.floor((abs % 3600) / 60)
  // Sub-minute residue truncates to nothing, so never render it as "-00:00".
  const sign = total < 0 && hours + minutes > 0 ? '-' : ''
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}`
}

const ZERO = '00:00'

/**
 * Aggregate work-period groups into per-week totals, aligned by index with the
 * provided week intervals. A shift is counted in every week it worked, for the
 * hours worked in that week; entries without a date are skipped. Salary-code
 * sums are grouped by name and ordered by salary id like the month table.
 */
export function getWeeklyWorkSums(
  groups: WeeklySumGroup[],
  weeks: WeekInterval[]
): WeeklyWorkSum[] {
  const entries = groups.flatMap(group => group.weekSums)

  return weeks.map(week => {
    const inWeek = entries.filter(
      entry => entry.date && isWithinInterval(entry.date, week)
    )

    const totalSeconds = inWeek.reduce(
      (acc, entry) => acc + timedeltaToSeconds(entry.totalWorkTime),
      0
    )

    const byName = new Map<string, { seconds: number; salaryId: string }>()
    inWeek.forEach(entry =>
      entry.groupedActivitySums.forEach(activitySum => {
        if (!activitySum.name) return
        const existing = byName.get(activitySum.name)
        byName.set(activitySum.name, {
          seconds:
            (existing?.seconds ?? 0) + timedeltaToSeconds(activitySum.sum),
          salaryId: existing?.salaryId ?? activitySum.salaryId ?? '',
        })
      })
    )

    const activitySums: WeeklyActivitySum[] = [...byName.entries()]
      .map(([name, { seconds, salaryId }]) => ({
        name,
        salaryId,
        sum: secondsToHourMinute(seconds),
      }))
      // Codes that cancel out over the week (and sub-minute residue) add noise.
      .filter(activitySum => activitySum.sum !== ZERO)
      .sort(
        (a, b) =>
          a.salaryId.localeCompare(b.salaryId) || a.name.localeCompare(b.name)
      )
      .map(({ name, sum }) => ({ name, sum }))

    const totalWorkTime = secondsToHourMinute(totalSeconds)

    return {
      totalWorkTime,
      activitySums,
      hasEntries:
        inWeek.length > 0 &&
        (totalWorkTime !== ZERO || activitySums.length > 0),
    }
  })
}
